import { useState, useEffect, useRef, useCallback } from "react";

// Custom hook for animating a number count up/down effect
// Parameters:
//  - start: Initial number value
//  - end: Target number value
//  - duration: Animation duration in ms (default 2000)
//  - onComplete: Optional callback function when animation completes

const useCountUp = ({ start, end, duration = 500, onComplete }) => {
  const [value, setValue] = useState(start);

  const startRef = useRef(start);
  const endRef = useRef(end);
  const intervalRef = useRef(null);

  const animate = useCallback(() => {
   if (intervalRef.current) clearInterval(intervalRef.current);

   const step = (endRef.current - startRef.current) / (duration / 50);
   let current = startRef.current;

   intervalRef.current = setInterval(() => {
     current += step;

     if ((step > 0 && current >= endRef.current) || (step < 0 && current <= endRef.current)) {
       setValue(endRef.current);
       clearInterval(intervalRef.current);
       intervalRef.current = null;
       if (onComplete) onComplete();
     } else {
       setValue(Math.round(current));
     }
   }, 50);
 }, [startRef, endRef, intervalRef, duration, setValue, onComplete]);


  useEffect(() => {
    startRef.current = start;
    endRef.current = end;
    animate();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [start, end, animate]);

  const reset = () => {
    setValue(startRef.current);
    animate();
  };

  return { value, reset };
};

export default useCountUp;
